import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Observable, Subscription, distinctUntilChanged, map, startWith } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  CommonBookingFeatureState,
  OrderActions,
  orderData,
  OrderPartStatus,
  QueueService,
  updatingOrderPart,
} from '@fcom/common-booking';
import { ModifiedServiceInfo, ModifiedServiceProgress } from '@fcom/dapi/interfaces';
import { Category, FinnairOrder, FinnairServiceItem } from '@fcom/dapi/api/models';
import { GtmService } from '@fcom/common/gtm';
import { ButtonPosition, ButtonTheme, IconPosition, LoaderTheme } from '@fcom/ui-components';
import { finShare, snapshot } from '@fcom/rx';
import { ElementActions, ElementTypes, FragmentService, GaContext, GtmEvent, GtmPurchaseFlow } from '@fcom/common';
import { setUpsell } from '@fcom/common-booking/store/actions/services.actions';
import { getServicesToShow } from '@fcom/common-booking/modules/ancillaries/utils';
import { CmsArticle } from '@fcom/core-api';
import { compareAsJson, unsubscribe } from '@fcom/core/utils';
import { getGA4Items } from '@fcom/common/utils/gtm.utils';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { ConfigService, RootPaths } from '@fcom/core';
import { LoginStatus } from '@fcom/core-api/login';
import { AllowedActionsMode } from '@fcom/common-booking/modules/ancillaries/enums';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { LanguageService } from '@fcom/ui-translate/services/language.service';

import { setStage } from '../../../store/actions/state.actions';
import { MmbStage } from '../../../enums';
import { ManageBookingService } from '../../../services';
import { removeSeatProduct, removeNonSeatProduct } from '../../../utils/ancillaries.util';

@Component({
  selector: 'fin-mmb-review-route',
  templateUrl: './review-route.component.html',
  styleUrls: ['./review-route.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingReviewRouteComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);
  private gtmService = inject(GtmService);
  private fb = inject(UntypedFormBuilder);
  private fragmentService = inject(FragmentService);
  private paymentQueueService = inject(QueueService);
  private feedbackService = inject(FeedbackService);
  private configService = inject(ConfigService);
  private manageBookingService = inject(ManageBookingService);
  private router = inject(Router);
  private languageService = inject(LanguageService);

  readonly ButtonPosition = ButtonPosition;
  readonly LoginStatus = LoginStatus;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly AllowedActionsMode = AllowedActionsMode;

  @HostBinding('class') classAttribute = 'col-f md-col-3-10';

  order$: Observable<FinnairOrder>;
  services$: Observable<FinnairServiceItem[]>;
  reviewForm: UntypedFormGroup;
  formValid$: Observable<boolean>;
  termAndCondition$: Observable<CmsArticle>;
  subscriptions: Subscription = new Subscription();
  disableTermsAndConditionCheckbox: boolean;
  priceUpdating: Signal<boolean>;
  cartIsEmpty$: Observable<boolean>;
  serviceModifyProgress: WritableSignal<ModifiedServiceProgress> = signal({
    modifyInProgress: false,
  });

  showLoader = computed(() => {
    return this.priceUpdating() && !this.serviceModifyProgress().modifyInProgress;
  });

  constructor() {
    this.priceUpdating = toSignal(
      this.store$.pipe(
        updatingOrderPart(),
        map(
          (statuses) =>
            !!statuses &&
            Object.keys(statuses).some((category: Category) => statuses[category] === OrderPartStatus.UPDATING)
        ),
        startWith(false),
        finShare()
      )
    );
  }

  ngOnInit(): void {
    this.disableTermsAndConditionCheckbox = this.configService.cfg.disableTermsAndConditionCheckbox;

    this.paymentQueueService.cancelOngoingPayment();
    this.order$ = this.store$.pipe(orderData(), finShare());
    this.cartIsEmpty$ = this.order$.pipe(
      map((o) => (o.services.unpaid ?? []).length === 0),
      finShare()
    );

    this.subscriptions.add(
      this.order$.pipe(distinctUntilChanged(compareAsJson)).subscribe((order) => {
        this.store$.dispatch(
          OrderActions.setOrderFetchInformation({ orderInformation: { id: order.id, hash: order.hash } })
        );
        if (this.disableTermsAndConditionCheckbox) {
          this.acceptTerms(true);
        }
        this.gtmService.ecommerceEventGA4(GtmEvent.VIEW_CART, getGA4Items(order, GtmPurchaseFlow.MANAGE_BOOKING, true));
      })
    );
    this.services$ = this.order$.pipe(
      map((booking) => getServicesToShow(booking.services, 'unpaid', false, true)),
      finShare()
    );
    this.reviewForm = this.fb.group({
      confirm: [this.disableTermsAndConditionCheckbox, Validators.requiredTrue],
    });

    this.formValid$ = this.reviewForm.statusChanges.pipe(
      map(() => this.reviewForm.valid),
      startWith(this.reviewForm.valid)
    );

    this.termAndCondition$ = this.fragmentService.getFragment<CmsArticle>(
      'MMB.fragments.ancillaryTermsAndConditions.url'
    );
    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  modifyClicked(serviceData: ModifiedServiceInfo): void {
    this.gtmService.trackElement(
      'review_modify',
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK,
      `recLoc-${snapshot(this.order$).otherInformation.analyticsToken}`
    );
    this.store$.dispatch(setStage({ stage: MmbStage.VIEW }));
    this.store$.dispatch(setUpsell({ category: serviceData.category, fragmentId: serviceData.fragmentId }));
  }

  payOrAdd(cartIsEmpty: boolean): void {
    if (cartIsEmpty) {
      this.store$.dispatch(setStage({ stage: MmbStage.VIEW }));
    } else {
      this.store$.dispatch(setStage({ stage: MmbStage.CHECKOUT }));
    }

    const element = cartIsEmpty ? 'continue-to-travel-extras' : 'continue-to-checkout';

    this.gtmService.trackElement(
      element,
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }

  acceptTerms(accept: boolean): void {
    this.store$.dispatch(OrderActions.acceptTerms({ accept: !!accept }));
  }

  removeProduct(serviceInfo: ModifiedServiceInfo): void {
    this.serviceModifyProgress.set({ services: [serviceInfo], modifyInProgress: true });
    const callbacks = {
      onSuccess: () => {
        this.serviceModifyProgress.set({
          modifyInProgress: false,
        });
      },
      onError: () => {
        this.serviceModifyProgress.set({
          modifyInProgress: false,
        });
      },
    };

    if (serviceInfo.category === Category.SEAT) {
      removeSeatProduct(serviceInfo, this.store$, this.manageBookingService, this.order$, callbacks);
    } else {
      removeNonSeatProduct(serviceInfo, this.store$, this.manageBookingService, this.order$, callbacks);
    }
  }

  backToMmb(): void {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`
    );
  }

  protected readonly ButtonTheme = ButtonTheme;
  protected readonly IconPosition = IconPosition;
}
